import { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { Button } from '../primitives/button';
import { Input } from '../primitives/input';
import { NativeSelect } from '../primitives/native-select';
import { Text } from '../typography/text';

type PaginationLabels = {
  showing?: (from: number, to: number, totalItems: number) => string;
  showingWithoutTotal?: (from: number, to: number) => string;
  page: string;
  of?: (totalPages: number) => string;
  previous?: string;
  next?: string;
};

type PaginationControlsBaseProps = {
  pageSize: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  labels: PaginationLabels;
  /** Restricts cursor-backed lists to pages whose cursor is currently known. */
  availablePages?: readonly number[];
  ariaLabel?: string;
  className?: string;
  disabled?: boolean;
  /** Adaptive keeps touch geometry whenever any available pointer is coarse. */
  size?: 'default' | 'touch' | 'adaptive';
  /**
   * A retained range shown while a replacement page is loading. It is visual
   * context only: page navigation still follows the current response props.
   */
  displayRange?: { from: number; to: number; totalItems?: number };
};

type ExactTotalPaginationControlsProps = PaginationControlsBaseProps & {
  totalItems: number;
  currentItemCount?: never;
  hasNextPage?: never;
};

type OmittedTotalPaginationControlsProps = PaginationControlsBaseProps & {
  totalItems?: never;
  currentItemCount: number;
  hasNextPage: boolean;
  hasPreviousPage?: boolean;
};

type PaginationControlsProps =
  | ExactTotalPaginationControlsProps
  | OmittedTotalPaginationControlsProps;

const buildPageRange = (totalItems: number, pageSize: number, currentPage: number) => {
  if (totalItems <= 0 || pageSize <= 0) {
    return { from: 0, to: 0 };
  }
  const from = (currentPage - 1) * pageSize + 1;
  const to = Math.min(currentPage * pageSize, totalItems);
  return { from, to };
};

const MAX_SELECT_PAGE_OPTIONS = 100;

export function PaginationControls(props: PaginationControlsProps) {
  const {
    pageSize,
    currentPage,
    onPageChange,
    labels,
    availablePages,
    ariaLabel,
    className,
    disabled = false,
    size = 'default',
    displayRange,
  } = props;
  const exactProps = props as ExactTotalPaginationControlsProps;
  const omittedProps = props as OmittedTotalPaginationControlsProps;
  const isExactTotal = props.totalItems !== undefined;
  const totalPages = isExactTotal ? Math.max(1, Math.ceil(exactProps.totalItems / pageSize)) : 1;
  const safePage = isExactTotal
    ? Math.min(Math.max(currentPage, 1), totalPages)
    : Math.max(currentPage, 1);
  const calculatedRange = isExactTotal
    ? buildPageRange(exactProps.totalItems, pageSize, safePage)
    : {
        from: omittedProps.currentItemCount === 0 ? 0 : (safePage - 1) * pageSize + 1,
        to:
          omittedProps.currentItemCount === 0
            ? 0
            : (safePage - 1) * pageSize + omittedProps.currentItemCount,
      };
  const { from, to } = displayRange ?? calculatedRange;
  const shouldUsePageInput =
    isExactTotal && availablePages === undefined && totalPages > MAX_SELECT_PAGE_OPTIONS;
  const pages = shouldUsePageInput
    ? []
    : (
        availablePages ??
        (isExactTotal ? Array.from({ length: totalPages }, (_, index) => index + 1) : [safePage])
      ).map(String);
  const [pageDraft, setPageDraft] = useState(String(safePage));
  const showingLabel =
    displayRange?.totalItems !== undefined
      ? (labels.showing?.(from, to, displayRange.totalItems) ?? '')
      : isExactTotal
        ? (labels.showing?.(from, to, exactProps.totalItems) ?? '')
        : (labels.showingWithoutTotal?.(from, to) ?? '');

  const isPrevDisabled =
    disabled ||
    (isExactTotal
      ? availablePages === undefined
        ? safePage <= 1
        : !availablePages.includes(safePage - 1)
      : omittedProps.hasPreviousPage !== true);
  const isNextDisabled =
    disabled ||
    (isExactTotal
      ? availablePages === undefined
        ? safePage >= totalPages
        : !availablePages.includes(safePage + 1)
      : !omittedProps.hasNextPage);
  const triggerWidthClass =
    (isExactTotal ? totalPages : Math.max(...(availablePages ?? [safePage]))) >= 1000
      ? 'w-16'
      : 'w-14';
  const buttonSizeClass = {
    default: 'h-7 w-7',
    touch: 'h-11 w-11',
    adaptive: 'h-7 w-7 [@media(any-pointer:coarse)]:h-11 [@media(any-pointer:coarse)]:w-11',
  }[size];
  const controlHeightClass = {
    default: 'h-6',
    touch: 'h-11',
    adaptive: 'h-6 [@media(any-pointer:coarse)]:h-11',
  }[size];

  useEffect(() => {
    setPageDraft(String(safePage));
  }, [safePage]);

  const commitPageDraft = () => {
    const parsed = Number(pageDraft);
    if (!Number.isFinite(parsed)) {
      setPageDraft(String(safePage));
      return;
    }

    const nextPage = Math.min(Math.max(Math.trunc(parsed), 1), totalPages);
    setPageDraft(String(nextPage));
    if (nextPage !== safePage) {
      onPageChange(nextPage);
    }
  };

  return (
    <div
      aria-label={ariaLabel}
      className={`flex flex-wrap items-center justify-end gap-2.5 ${className ?? ''}`}
    >
      {showingLabel ? (
        <Text size="sm" color="muted" className={displayRange ? 'opacity-60' : undefined}>
          {showingLabel}
        </Text>
      ) : null}
      <div
        className={
          size !== 'default'
            ? 'flex max-w-full flex-wrap items-center gap-2'
            : 'flex items-center gap-2'
        }
      >
        <Button
          type="button"
          variant="ghost"
          className={`${buttonSizeClass} rounded-none border border-border p-0 text-muted-foreground hover:bg-muted`}
          disabled={isPrevDisabled}
          aria-label={labels.previous ?? 'Previous page'}
          onClick={() => {
            if (!isPrevDisabled) {
              onPageChange(safePage - 1);
            }
          }}
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
        </Button>
        <Text size="sm" color="muted">
          {labels.page}
        </Text>
        {shouldUsePageInput ? (
          <Input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={pageDraft}
            disabled={disabled}
            aria-label={labels.page}
            className={`${controlHeightClass} ${triggerWidthClass} rounded-[2px] border border-border px-2 py-0 text-sm font-normal`}
            onChange={(event) => {
              const nextValue = event.target.value.trim();
              if (nextValue === '' || /^\d+$/.test(nextValue)) {
                setPageDraft(nextValue);
              }
            }}
            onBlur={commitPageDraft}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.currentTarget.blur();
              }
              if (event.key === 'Escape') {
                setPageDraft(String(safePage));
                event.currentTarget.blur();
              }
            }}
          />
        ) : (
          <NativeSelect
            value={String(safePage)}
            onChange={(event) => {
              onPageChange(Number(event.target.value));
            }}
            disabled={disabled}
            aria-label={labels.page}
            containerClassName={triggerWidthClass}
            className={`${controlHeightClass} rounded-[2px] border border-border px-2 py-0 pr-6 text-sm font-normal`}
            iconClassName="right-1 h-3 w-3"
            data-pagination-page-select="native"
          >
            {pages.map((page) => (
              <option key={page} value={page}>
                {page}
              </option>
            ))}
          </NativeSelect>
        )}
        {isExactTotal && labels.of !== undefined ? (
          <Text size="sm" color="muted">
            {labels.of(totalPages)}
          </Text>
        ) : null}
        <Button
          type="button"
          variant="ghost"
          className={`${buttonSizeClass} rounded-none border border-border p-0 text-muted-foreground hover:bg-muted`}
          disabled={isNextDisabled}
          aria-label={labels.next ?? 'Next page'}
          onClick={() => {
            if (!isNextDisabled) {
              onPageChange(safePage + 1);
            }
          }}
        >
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>
    </div>
  );
}
